/**
 * Two small modals the Security / Users containers need and the old app never had (D-036 re-enrolment,
 * D-037 one-time setup link). Frame classes copied from the extracted PinDialog (backdrop, card, dark
 * header, buttons) so they belong to the same dialog family; content blocks reuse the Login enrolment
 * step's classes. Candidates for packages/ui in B4.
 */
import { KeyRound, Link2, ShieldCheck, X } from "lucide-react";
import { TotpQr, TotpSecretText, useT } from "@faylasuf/ui";

const PIN_INPUT = "w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all";
const LABEL = "block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest";
const MONO_BOX = "w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-center font-mono font-black text-gray-900 dark:text-[#f0f6fc] break-all";

function Frame({ title, icon, onClose, testId, children }: { title: string; icon: React.ReactNode; onClose: () => void; testId: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={testId}>
      <div onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border">
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-900 text-white dark:bg-[#161b22]">
          <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
            {icon}
            {title}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}

export interface EnrolDialogProps {
  readonly enrolment: { readonly secret: string; readonly uri: string } | null;
  readonly code: string;
  readonly onCodeChange: (v: string) => void;
  readonly recoveryCodes: readonly string[] | null;
  readonly error?: string | null;
  readonly busy?: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

export function EnrolDialog(p: EnrolDialogProps) {
  const t = useT();
  return (
    <Frame title={t("security.enrol_dialog_title")} icon={<KeyRound className="text-rose-500" />} onClose={p.onClose} testId="enrol-dialog">
      {p.recoveryCodes ? (
        <>
          <label className={LABEL}>{t("login.recovery_codes_title")}</label>
          <div data-testid="enrol-recovery-codes" dir="ltr" className={`${MONO_BOX} grid grid-cols-2 gap-2`}>
            {p.recoveryCodes.map((c) => <span key={c}>{c}</span>)}
          </div>
          <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] text-center">{t("login.recovery_codes_hint")}</p>
          <button type="button" onClick={p.onClose} data-testid="enrol-done" className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black transition-all shadow-lg shadow-rose-600/20 text-xs uppercase tracking-widest">
            {t("login.continue")}
          </button>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!p.busy && p.code.length === 6) p.onConfirm();
          }}
          className="space-y-4"
        >
          <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] text-center">{t("login.enrol_hint")}</p>
          {p.enrolment?.uri ? <TotpQr uri={p.enrolment.uri} px={176} label={t("login.enrol_qr")} testId="enrol-qr" /> : null}
          <div>
            <label className={LABEL}>{t("login.enrol_manual")} — {t("login.enrol_secret")}</label>
            <TotpSecretText secret={p.enrolment?.secret ?? ""} testId="enrol-secret" className={`${MONO_BOX} tracking-[0.1em] select-all`} />
            <p className="text-[11px] font-bold text-gray-400 dark:text-[#8b949e] mt-2 text-center">{t("login.enrol_alphabet")}</p>
          </div>
          <div>
            <label className={LABEL}>{t("login.second_factor_code")}</label>
            <input type="text" inputMode="numeric" maxLength={6} autoFocus autoComplete="one-time-code" data-testid="enrol-code" value={p.code} onChange={(e) => p.onCodeChange(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" className={PIN_INPUT} />
          </div>
          {p.error && <p data-testid="enrol-error" className="text-xs font-bold text-rose-500 text-center">{p.error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={p.onClose} className="w-1/2 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-[#161b22] dark:hover:bg-[#21262d] text-gray-700 dark:text-gray-300 rounded-xl font-bold transition-all text-xs">
              {t("pin.cancel")}
            </button>
            <button type="submit" disabled={p.busy || p.code.length !== 6 || !p.enrolment} data-testid="enrol-confirm" className="w-1/2 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black transition-all shadow-lg shadow-rose-600/20 disabled:opacity-50 text-xs uppercase tracking-widest">
              {p.busy ? t("pin.verifying") : t("login.enrol_confirm")}
            </button>
          </div>
        </form>
      )}
    </Frame>
  );
}

export function LinkDialog({ title, link, onClose }: { title: string; link: string; onClose: () => void }) {
  const t = useT();
  return (
    <Frame title={title} icon={<Link2 className="text-rose-500" />} onClose={onClose} testId="link-dialog">
      <label className={LABEL}>{t("users.setup_link")}</label>
      <div dir="ltr" data-testid="setup-link" className={`${MONO_BOX} text-xs select-all`}>{link}</div>
      <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-amber-600 dark:text-amber-400">
        <ShieldCheck size={14} />
        {t("security.reset_access_done")}
      </div>
      <button type="button" onClick={onClose} data-testid="link-done" className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black transition-all shadow-lg shadow-rose-600/20 text-xs uppercase tracking-widest">
        {t("common.close")}
      </button>
    </Frame>
  );
}

/** The link a manager hands to the user (D-037); the API returns the token only (users.ts:87, :198). */
export function setupLinkFor(token: string): string {
  return `${window.location.origin}/setup/${token}`;
}
